import type { InventoryItem, Work } from '@app/types/shop-bff/types';
import { getDiscountValue } from '@app/utils/common';
import type {
  RSCheckoutLocationKey,
  RSCheckoutStepKey,
  RSProductClickedLocationKey,
  RSProductClickedPageTypeKey,
  RudderstackBrowserClient,
} from '@redbubble/next-provider/rudderstack';
import { getItemValue, mapLegacyAttributes } from './utils';

interface IRudderstackTrack {
  rsClient?: RudderstackBrowserClient;
}

export const trackRSProductViewed = ({
  rsClient,
  inventoryItem,
  work,
}: IRudderstackTrack & {
  inventoryItem: InventoryItem;
  work: Work;
}) => {
  if (!rsClient) return;

  rsClient.productViewed({
    product_id: String(inventoryItem.marketingProductTypeId),
    name: `${work.title} ${inventoryItem.description}`,
    price: inventoryItem.price?.amount,
    currency: inventoryItem.price?.currency,
    sku: inventoryItem.id,
    category: inventoryItem.productTypeId,
    image_url: inventoryItem.previewSet?.previews?.[0]?.url,
    url: String(inventoryItem.productPageUrl),
    product_type_id: inventoryItem.productTypeId,
    blank_id: inventoryItem.blankItemId,
    work_id: String(inventoryItem.workId),
    quantity: 1,
    // We flatten the attributes as root level properties when we send to Segment
    // https://github.com/redbubble/segment-lambdas/blob/bee89cd0268a8ecf0a464f99cd8beabcd16a28f2/app/adapters/InventoryItemAdapter.js#L52-L57
    legacy_product_details: mapLegacyAttributes({
      attributes: inventoryItem.attributes,
    }),
    discount: getDiscountValue(inventoryItem.price),
    value: inventoryItem.price ? getItemValue(inventoryItem.price) : undefined,
    // Following don't exist yet in the schema. But are mix of property that exist in Segment and GA4 schemas
    // https://github.com/redbubble/segment-lambdas/blob/bee89cd0268a8ecf0a464f99cd8beabcd16a28f2/app/adapters/InventoryItemAdapter.js
    // @ts-ignore
    is_licensed: work.licensed,
    // @ts-ignore
    is_safe_for_work: !work.matureContent,
  });
};

export const trackRSCheckoutStepSubmitted = ({
  rsClient,
  checkoutStep,
}: IRudderstackTrack & {
  checkoutStep: RSCheckoutStepKey;
}) => {
  if (!rsClient) return;

  rsClient.checkoutStepSubmitted({
    checkout_step: checkoutStep,
  });
};

export const trackRSCheckoutClicked = ({
  rsClient,
  checkoutType,
  location,
}: IRudderstackTrack & {
  checkoutType: string;
  location: RSCheckoutLocationKey;
}) => {
  if (!rsClient) return;

  const rsCheckoutTypeProperty = checkoutType.replaceAll('_', '-');

  rsClient.checkoutClicked({
    checkout_type: rsCheckoutTypeProperty,
    location: location,
  });
};

export const trackRSProductClicked = ({
  rsClient,
  blankId: blank_id,
  brand,
  category,
  country,
  coupon,
  currency,
  imageUrl: image_url,
  legacyProductDetails: legacy_product_details,
  locale,
  location,
  name,
  pageType: page_type,
  position,
  price,
  productId: product_id,
  productTypeId: product_type_id,
  quantity,
  sku,
  url,
  variant,
  workId: work_id,
}: IRudderstackTrack & {
  blankId?: string;
  brand?: string;
  category?: string;
  country?: string;
  coupon?: string;
  currency?: string;
  imageUrl?: string;
  legacyProductDetails?: Record<string, any>;
  locale?: string;
  location: RSProductClickedLocationKey;
  name?: string;
  pageType: RSProductClickedPageTypeKey;
  position?: number;
  price?: number;
  productId?: string;
  productTypeId?: string;
  quantity?: number;
  sku?: string;
  url?: string;
  variant?: string;
  workId?: number | string;
}) => {
  if (!rsClient) return;

  rsClient.productClicked({
    blank_id,
    brand,
    category,
    country,
    coupon,
    currency,
    image_url,
    legacy_product_details,
    locale,
    location,
    name,
    page_type,
    position,
    price,
    product_id,
    product_type_id,
    quantity,
    sku,
    url,
    variant,
    work_id,
  });
};
